import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Sparkles, Play } from 'lucide-react';
import { TOUR_STEPS } from '../lib/tourSteps';

const STORAGE_KEY = 'iris_tour_state';

// ─── Persistence helpers ─────────────────────────────────────────
const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { /* ignore corrupt localStorage */ return null; }
};
const saveState = (s) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* quota / privacy mode */ } };
const clearState = () => { try { localStorage.removeItem(STORAGE_KEY); } catch { /* quota / privacy mode */ } };

// ─── Hook to expose start/stop to the rest of the app ─────────────
export function useTourControl() {
  const startTour = useCallback((atIdx = 0) => {
    saveState({ active: true, idx: atIdx, ts: Date.now() });
    window.dispatchEvent(new CustomEvent('iris-tour-refresh'));
  }, []);
  const stopTour = useCallback(() => {
    clearState();
    window.dispatchEvent(new CustomEvent('iris-tour-refresh'));
  }, []);
  return { startTour, stopTour };
}

// ─── Overlay component — mount once at layout root ────────────────
export default function PlatformTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(loadState());
  const [targetRect, setTargetRect] = useState(null);
  const observerRef = useRef(null);

  // Re-read state on custom event (from useTourControl or manual clicks)
  useEffect(() => {
    const refresh = () => setState(loadState());
    window.addEventListener('iris-tour-refresh', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('iris-tour-refresh', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  // Persist state changes
  useEffect(() => {
    if (state?.active) saveState(state);
    else clearState();
  }, [state]);

  const active = state?.active;
  const idx = state?.idx ?? 0;
  const step = active ? TOUR_STEPS[idx] : null;

  // Auto-navigate to step's path when idx changes or step's path changes
  useEffect(() => {
    if (!active || !step) return;
    if (location.pathname !== step.path) {
      navigate(step.path);
    }
  }, [active, idx, step, location.pathname, navigate]);

  // Locate the target element on the current page (retries via MutationObserver
  // because pages may lazily render their content)
  useEffect(() => {
    if (!active || !step) { setTargetRect(null); return; }
    if (!step.target) { setTargetRect(null); return; }
    if (location.pathname !== step.path) return;   // wait for route change

    const findAndMeasure = () => {
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setTargetRect({
          top: r.top + window.scrollY,
          left: r.left + window.scrollX,
          width: r.width,
          height: r.height,
        });
        // Auto-scroll target into view (centered vertically)
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch { /* older browsers */ }
        return true;
      }
      return false;
    };

    // Immediate attempt + retry via mutation observer for lazy content
    if (findAndMeasure()) return;

    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new MutationObserver(() => { findAndMeasure(); });
    observerRef.current.observe(document.body, { childList: true, subtree: true });
    const timeout = setTimeout(() => {
      if (observerRef.current) observerRef.current.disconnect();
    }, 4000);   // stop looking after 4s

    return () => {
      clearTimeout(timeout);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [active, step, idx, location.pathname]);

  // Recompute rect on window resize
  useEffect(() => {
    if (!active || !step?.target) return;
    const onResize = () => {
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setTargetRect({
          top: r.top + window.scrollY, left: r.left + window.scrollX,
          width: r.width, height: r.height,
        });
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [active, step]);

  // Keyboard nav
  useEffect(() => {
    if (!active) return;
    const h = (e) => {
      if (['INPUT','TEXTAREA'].includes(e.target?.tagName)) return;
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); prev(); }
      if (e.key === 'Escape')     { e.preventDefault(); stop(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });   // no deps — recompute each render, cheap

  const next = () => {
    setState(s => {
      if (!s) return s;
      if (s.idx >= TOUR_STEPS.length - 1) { clearState(); return null; }
      return { ...s, idx: s.idx + 1 };
    });
  };
  const prev = () => setState(s => s ? { ...s, idx: Math.max(0, s.idx - 1) } : s);
  const stop = () => { clearState(); setState(null); };

  if (!active || !step) return null;

  const total = TOUR_STEPS.length;
  const pct = ((idx + 1) / total) * 100;

  // Positioning: spotlight box around target rect (if any), tooltip anchored top-right
  return (
    <div className="pointer-events-none" data-testid="platform-tour-overlay">
      {/* Dim backdrop with spotlight cutout */}
      {targetRect ? (
        <>
          {/* 4 dark rectangles around the target (creates a "hole") */}
          <div className="fixed bg-black/50 z-[9990] pointer-events-none"
               style={{ top: 0, left: 0, right: 0, height: targetRect.top - window.scrollY - 8 }}/>
          <div className="fixed bg-black/50 z-[9990] pointer-events-none"
               style={{ top: targetRect.top - window.scrollY + targetRect.height + 8, left: 0, right: 0, bottom: 0 }}/>
          <div className="fixed bg-black/50 z-[9990] pointer-events-none"
               style={{ top: targetRect.top - window.scrollY - 8, left: 0,
                        width: targetRect.left - window.scrollX - 8,
                        height: targetRect.height + 16 }}/>
          <div className="fixed bg-black/50 z-[9990] pointer-events-none"
               style={{ top: targetRect.top - window.scrollY - 8,
                        left: targetRect.left - window.scrollX + targetRect.width + 8,
                        right: 0, height: targetRect.height + 16 }}/>
          {/* Highlight ring */}
          <div
            className="fixed z-[9991] pointer-events-none rounded-lg transition-all duration-300"
            style={{
              top: targetRect.top - window.scrollY - 6,
              left: targetRect.left - window.scrollX - 6,
              width: targetRect.width + 12,
              height: targetRect.height + 12,
              boxShadow: '0 0 0 3px #679286, 0 0 24px 4px rgba(103,146,134,0.5)',
            }}
          />
        </>
      ) : (
        // No target: full-screen dim
        <div className="fixed inset-0 bg-black/60 z-[9990] pointer-events-none"/>
      )}

      {/* Tooltip card — always bottom-right, pointer events enabled */}
      <div
        className="fixed bottom-6 right-6 max-w-md z-[9999] pointer-events-auto"
        data-testid="tour-tooltip"
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#679286]/10 grid place-items-center">
                <Sparkles size={13} className="text-[#679286]"/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#679286] font-semibold">
                  {step.chapter}
                </div>
                <div className="text-[10px] text-slate-400 tabular-nums">
                  {idx + 1} / {total}
                </div>
              </div>
            </div>
            <button
              data-testid="tour-close"
              onClick={stop}
              className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
              title="Exit tour (Esc)"
            >
              <X size={16}/>
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            <h3 className="font-serif text-xl text-slate-900 leading-tight mb-2">
              {step.title}
            </h3>
            <p className="text-sm text-slate-700 leading-relaxed mb-3" data-testid="tour-body">
              {step.body}
            </p>
            {step.hint && (
              <div className="text-[11px] text-slate-500 italic border-l-2 border-[#679286]/40 pl-2 py-0.5">
                {step.hint}
              </div>
            )}
          </div>

          {/* Progress + nav */}
          <div className="px-5 pb-4">
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-3">
              <div className="h-1 bg-[#679286] transition-all duration-500"
                   style={{ width: `${pct}%` }}/>
            </div>
            <div className="flex items-center justify-between gap-2">
              <button
                data-testid="tour-prev"
                onClick={prev}
                disabled={idx === 0}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-30"
              >
                <ChevronLeft size={12}/> Back
              </button>
              <div className="text-[10px] text-slate-400">
                <kbd className="px-1 bg-slate-100 rounded text-[9px]">→</kbd> next  <kbd className="px-1 bg-slate-100 rounded text-[9px]">Esc</kbd> exit
              </div>
              <button
                data-testid="tour-next"
                onClick={next}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-slate-900 text-white hover:bg-slate-800"
              >
                {idx === total - 1 ? 'Finish' : 'Next'} <ChevronRight size={12}/>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Small standalone launcher (used from the Present page) ─────
export function StartTourButton({ className = '' }) {
  const { startTour } = useTourControl();
  return (
    <button
      data-testid="start-tour-btn"
      onClick={() => startTour(0)}
      className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white hover:bg-slate-800 font-medium ${className}`}
    >
      <Play size={16}/> Start Interactive Tour
    </button>
  );
}
